/**
 * Service for Domain Configuration (Web)
 * Public endpoint to get domain-specific config (languages, countries, etc.)
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// La version est dans la clé : une session ouverte avant l'arrivée de
// moduleCode porte un objet sans ce champ, et le relire ferait passer une
// adresse d'app pour un domaine de plateforme — donc afficher tous les
// modules. Changer la clé périme ces entrées sans code de migration.
const CACHE_KEY = 'cabazaDomainConfig.v2';

/**
 * Get domain configuration for a hostname
 * Caches result in sessionStorage to avoid repeated API calls
 * @param {string} hostname - The domain hostname (e.g. "www.cabaza.be")
 * @returns {Promise<Object>} Domain config with { found, domainName, label, defaultLanguage, languages, countries }
 */
export const getDomainConfig = async (hostname) => {
    try {
        // Check sessionStorage cache
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.hostname === hostname) {
                return parsed.config;
            }
        }

        const response = await fetch(`${API_BASE_URL}/domains/config?domain=${encodeURIComponent(hostname)}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;

        if (success && data) {
            const config = {
                found: true,
                domainName: data.domainName ?? data.DOMAINNAME ?? data.domain_name ?? data.DOMAIN_NAME ?? '',
                label: data.label ?? data.LABEL ?? '',
                defaultLanguage: data.defaultLanguage ?? data.DEFAULTLANGUAGE ?? data.default_language ?? data.DEFAULT_LANGUAGE ?? '',
                languages: data.languages ?? data.LANGUAGES ?? [],
                countries: data.countries ?? data.COUNTRIES ?? [],
                // Le module rattaché au domaine : vide pour la plateforme,
                // rempli pour une adresse d'app. C'est lui qui décide du
                // périmètre visible (DomainContext).
                moduleCode: data.moduleCode ?? data.MODULECODE ?? data.modulecode ?? ''
            };

            // Cache in sessionStorage
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                hostname,
                config
            }));

            return config;
        }

        return { found: false };
    } catch (error) {
        console.error('Error fetching domain config:', error);
        return { found: false };
    }
};
